import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useFilePicker } from 'use-file-picker';
import { ScrollMenu } from 'react-horizontal-scrolling-menu';
import { t } from 'i18next';

import { useQueryParams } from '@pocapan/shared/utils/useQueryParams';
import { useHandleHistoryBack } from '@pocapan/shared/utils/useHandleHistoryBack';
import { resizeToBase64 } from '@pocapan/shared/utils/resizeImage';
import isResizableMimetype from '@pocapan/shared/utils/ImageUtils';

import StickyHeader from '../../../components/StickyHeader';
import { ReportFootprintBreadcrumb } from '../../../components/report/ReportFootprintBreadcrumb';
import StepComponent from '../../../components/steps/StepComponent';

import { Camera, Xmark } from '@peacecover/icon';
import { Button } from '@peacecover/component';
import { useReportEventAddDetailsStore } from '../../../presentation/report/store/useReportEventAddDetailsStore';
import { updateEventDetail } from '../../../data/repositories/UpdateEventDetail';

const ReportEventAddDetails = () => {
  const navigate = useNavigate();
  const { queryParams } = useQueryParams();
  const locationId = queryParams.get('locationId') || '';
  const eventId = queryParams.get('eventId') || '';

  const [isLoading, setIsLoading] = useState(false);
  const {
    posterDataUrls,
    description,
    addPosterDataUrls,
    removePosterDataUrl,
    setDescription,
    clear
  } = useReportEventAddDetailsStore();

  const handleHistoryBack = useHandleHistoryBack();
  const { openFilePicker, filesContent, plainFiles } = useFilePicker({
    readAs: 'DataURL',
    accept: 'image/*',
    multiple: true
  });

  const handleAddImageClick = () => {
    if (posterDataUrls.length >= 10) return;
    openFilePicker();
  };

  const onDone = async () => {
    if (posterDataUrls.length === 0 && description.length === 0) {
      return;
    }
    setIsLoading(true);
    try {
      const response = await updateEventDetail({
        locationId,
        eventId,
        description,
        posterDataUrls
      });
      if (response.ok) {
        clear();
        navigate('/collections?boxTabIndex=1');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreview = () => {
    if (!locationId || !eventId) return;
    window.open(`/locations/${locationId}/events/${eventId}?newTabOpen=true`, '_blank');
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: 의존성 배열 추가 시 이미지 추가 함수 재실행됨
  useEffect(() => {
    let cancelled = false;

    const resizeAndAdd = async () => {
      if (!filesContent || filesContent.length === 0) return;

      const remainingSlots = Math.max(0, 10 - posterDataUrls.length);
      if (remainingSlots === 0) return;

      const tasks = (filesContent as Array<{ content: string }>)
        .slice(0, remainingSlots)
        .map((f, index) => {
          const file = (plainFiles as File[] | undefined)?.[index];
          if (file && isResizableMimetype(file.type)) {
            return resizeToBase64(file);
          }
          return Promise.resolve((f.content ?? '') as string);
        });

      const resized = await Promise.all(tasks);
      if (cancelled) return;

      const next = resized.filter(Boolean) as string[];
      const deduped = next.filter((url) => !posterDataUrls.includes(url));

      if (deduped.length > 0) {
        addPosterDataUrls(deduped);
      }
    };

    resizeAndAdd();

    return () => {
      cancelled = true;
    };
  }, [filesContent, plainFiles, addPosterDataUrls]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: 클린업 함수
  useEffect(() => {
    return () => {
      clear();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>{t('ReportEventAddDetails.pageName', { defaultValue: '제보하기' })}</title>
      </Helmet>
      <StickyHeader
        title={t('ReportEventAddDetails.pageName', { defaultValue: '제보하기' }) as string}
        alwaysShowTitle={true}
        showBackButton={false}
        showTitle={true}
        leftContent={<Xmark size={24} className="text-text-main-lv1" onClick={handleHistoryBack} />}
      />
      <div className="relative min-h-[100lvh] flex flex-col pt-[calc(54px+env(safe-area-inset-top))] pb-[calc(128px+env(safe-area-inset-bottom))]">
        <div className="px-5 py-[24px]">
          <StepComponent currentStep={0} nextStep={1} maxStep={5} title="">
            <div className="px-1 flex flex-col gap-[18px]">
              <div className="mx-[-24px]">
                <ReportFootprintBreadcrumb mergeAll={true} />
              </div>
              <h1 className="text-[26px] font-bold text-text-main-lv1 leading-tight whitespace-pre-wrap">
                {t('ReportEventAddDetails.title', {
                  defaultValue: '추가할 내용이 있다면\n입력해주세요'
                })}
              </h1>
            </div>
            <p className="text-[17px] font-semibold text-text-main-lv1 pt-[12px] pb-[6px] mt-[26px]">
              {t('ReportEventAddDetails.postImageTitle', {
                defaultValue: '상세사진이 있다면 추가해주세요.'
              })}
            </p>
            <div className="relative">
              <ScrollMenu wrapperClassName="poster-scroll-container">
                <button
                  type="button"
                  className={`border border-separator-light-1 dark:border-separator-dark-1 w-[75px] h-[75px] flex flex-col justify-center items-center mt-[12px] rounded-[12px] bg-transparent ${
                    posterDataUrls.length >= 10 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  onClick={handleAddImageClick}
                  disabled={posterDataUrls.length >= 10}
                >
                  <Camera className="fill-black dark:fill-white" />
                  <div className="text-[12px] text-sub-light-1 dark:text-sub-dark-1">
                    {posterDataUrls.length}/10
                  </div>
                </button>
                <div className="flex pt-[12px]">
                  {posterDataUrls.map((url, idx) => (
                    <div
                      key={url + crypto.randomUUID()}
                      className="w-[75px] h-[75px] relative ml-[12px]"
                    >
                      <img
                        src={url}
                        alt={'Attached'}
                        className="w-[75px] h-[75px] rounded-[8px] object-cover max-w-full max-h-full"
                      />
                      <button
                        type="button"
                        className="absolute top-[-10px] -right-[10px] rounded-full p-[5px] cursor-pointer inline-flex bg-primary-light dark:bg-primary-dark"
                        onClick={() => removePosterDataUrl(idx)}
                      >
                        <Xmark size={14} className="text-on-primary" />
                      </button>
                    </div>
                  ))}
                </div>
              </ScrollMenu>
            </div>
          </StepComponent>
          <StepComponent currentStep={0} nextStep={1} maxStep={5} title="">
            <p className="text-[17px] font-semibold text-text-main-lv1 pt-[12px] pb-[6px]">
              {t('ReportEventAddDetails.postDescriptionTitle', {
                defaultValue: '더 알려주고 싶은 정보가 있다면 적어주세요'
              })}
            </p>
            <textarea
              className="w-full bg-secondary-light dark:bg-secondary-dark placeholder:text-placeholder-light dark:placeholder:text-placeholder-dark rounded-[12px] p-[20px] text-[15px] font-medium min-h-[120px] -mx-[4px] my-[10px] outline-none"
              placeholder={
                t('ReportEventAddDetails.postDescriptionPlaceholder', {
                  defaultValue: '여기를 눌러서 입력해 주세요'
                }) as string
              }
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </StepComponent>
        </div>
        <div
          className={
            'absolute bottom-0 left-0 right-0 bg-background-light dark:bg-background-dark text-center z-[9999] flex gap-[7px]'
          }
          style={{
            padding: '10px 24px calc(10px + env(safe-area-inset-bottom)) 24px'
          }}
        >
          <Button
            size="large"
            btnColor="secondary"
            className="w-full font-semibold text-[17px]"
            onClick={handlePreview}
            disabled={isLoading}
          >
            {t('ReportEventAddDetails.preview', {
              defaultValue: '미리보기'
            })}
          </Button>
          <Button
            size="large"
            btnColor="primary"
            onClick={onDone}
            className="w-full font-semibold text-[17px]"
            disabled={isLoading}
          >
            {t('done')}
          </Button>
        </div>
      </div>
    </>
  );
};

export default ReportEventAddDetails;
